import React, { useState } from 'react';
import { Form, Input, Button, Alert, App, Typography } from 'antd';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import accountDeletionService from '../../services/accountDeletion';
import { AUTHORIZATION_TEXT } from './deleteAccountContent';

const { Text, Paragraph } = Typography;

export default function DeleteAccountForm() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const { modal } = App.useApp();

  const handleInputChange = () => {
    if (globalError) {
      setGlobalError(null);
    }
  };

  const submitDeletion = async (values) => {
    setLoading(true);
    setGlobalError(null);

    try {
      await accountDeletionService.requestAccountDeletion({
        email: values.email,
      });
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      const errorCode = data?.code || err?.code;

      if (status === 401 || status === 422 || errorCode === 'INVALID_CREDENTIALS') {
        setGlobalError(
          'Correo incorrecto o cuenta no encontrada. Verifica el correo e inténtalo de nuevo.',
        );
      } else if (status === 403) {
        setGlobalError(
          'Tu cuenta no puede eliminarse por este formulario. Si necesitas ayuda, contacta a soporte.',
        );
      } else if (status >= 500) {
        setGlobalError('Error del servidor. Por favor, inténtalo más tarde.');
      } else if (!navigator.onLine) {
        setGlobalError('Sin conexión a internet. Verifica tu conexión e inténtalo de nuevo.');
      } else {
        const fieldErrors = data?.fieldErrors || data?.errors || {};
        const fields = Object.entries(fieldErrors).map(([name, errors]) => ({
          name,
          errors: Array.isArray(errors) ? errors : [String(errors || 'Error')],
        }));

        if (fields.length) {
          form.setFields(fields);
        } else {
          setGlobalError(
            data?.message || err?.message || 'No se pudo procesar la solicitud. Inténtalo de nuevo.',
          );
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const onFinish = (values) => {
    modal.confirm({
      title: '¿Confirmar eliminación de cuenta?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Esta acción es irreversible. Perderás el acceso a tu cuenta, rutinas, progreso y funciones asociadas.',
      okText: 'Sí, eliminar mi cuenta',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () => submitDeletion(values),
    });
  };

  if (submitted) {
    return (
      <div className="support-form-card delete-account-success">
        <CheckCircleOutlined
          style={{ fontSize: 48, color: 'var(--sthenus-primary)', marginBottom: 16 }}
        />
        <Paragraph strong style={{ fontSize: 16, marginBottom: 8 }}>
          Tu solicitud fue enviada correctamente.
        </Paragraph>
        <Text type="secondary">
          Revisa tu correo electrónico para confirmar la eliminación de tu cuenta. Si tienes dudas,
          puedes contactar a soporte.
        </Text>
        <div className="delete-account-form-actions" style={{ justifyContent: 'center', marginTop: 24 }}>
          <Button type="primary" onClick={() => navigate('/login')}>
            Regresar al inicio
          </Button>
          <Button onClick={() => navigate('/support')}>Centro de ayuda</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="support-form-card">
      {globalError && (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
          message="No se pudo eliminar la cuenta"
          description={
            <>
              {globalError}
              {globalError.includes('soporte') && (
                <>
                  {' '}
                  <a
                    href="/support"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/support');
                    }}
                  >
                    Ir al centro de ayuda
                  </a>
                </>
              )}
            </>
          }
          closable
          onClose={() => setGlobalError(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        validateTrigger={['onBlur', 'onSubmit']}
        autoComplete="off"
      >
        <Form.Item
          label="Correo electrónico"
          name="email"
          rules={[
            { required: true, message: 'El correo electrónico es obligatorio.' },
            { type: 'email', message: 'Formato de correo inválido.' },
          ]}
        >
          <Input
            size="large"
            placeholder="tu@email.com"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={handleInputChange}
          />
        </Form.Item>

        <Paragraph className="delete-account-legal">{AUTHORIZATION_TEXT}</Paragraph>

        <Form.Item style={{ marginBottom: 0 }}>
          <div className="delete-account-form-actions">
            <Button
              type="primary"
              danger
              htmlType="submit"
              size="large"
              loading={loading}
            >
              Solicitar eliminación
            </Button>
            <Button size="large" onClick={() => navigate('/login')}>
              Regresar al inicio
            </Button>
            <Button size="large" onClick={() => navigate('/support')}>
              Necesito ayuda
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
}
